import React, { useState } from "react";
import { useIntl } from "react-intl";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import { MdDelete, MdClose, MdCreate, MdFileUpload } from "react-icons/md";
import { trackEvent } from "~/utils/analytics";
import MapCache from "~/components/MapCache";
import { processGlebasCarAoiFile } from "~/api";
import "./styles.scss";

const AddAreaToCarModal = ({
  operationId,
  setError,
  trackId,
  onSuccess,
  onClose,
  options
}) => {
  const { formatMessage } = useIntl();

  const [filename, setFilename] = useState(null);
  const [areas, setAreas] = useState(null);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [step, setStep] = useState("start");

  const [isLoading, setIsLoading] = useState(false);

  const downloadShapeFile = () => {
    trackEvent("download Shapefile");
    const url = "https://buscar.terras.app/app/files/template_area.zip";
    window.open(url, "_blank");
  };

  const downloadKml = () => {
    trackEvent("download Kml");
    const url = "https://buscar.terras.app/app/files/template_area.kml";
    window.open(url, "_blank");
  };

  const uploadFile = async e => {
    if (!e.target.files) return;

    const file = e.target.files[0];

    const formData = new FormData();
    formData.append("file_contents", file);

    const uploadRes = await processGlebasCarAoiFile(formData);

    const uploadData = await Promise.resolve(uploadRes);
    const result = uploadData.data.result;

    setAreas(result);
    if (result?.features?.length === 1) {
      setSelectedAreas([0]);
    }

    setFilename(file.name);
  };

  const saveAreas = async () => {
    setIsLoading(true);
    const featuresToSave = (areas.features ?? []).filter((_, featureIdx) =>
      selectedAreas.includes(featureIdx)
    );

    const geojson = {
      ...areas,
      features: featuresToSave
    };

    try {
      const params = {
        b3PropostaId: operationId,
        geojson,
        trackId
      };

      const response = await options.save(params);

      if (response.data.message) {
        setError(response.data.message);
      }

      onSuccess("uploaded");
      setIsLoading(false);
    } catch (e) {
      setError(e.response?.data?.message ?? formatMessage({ id: "erro-api" }));
      setIsLoading(false);
      onClose();
    }
  };

  let mapCenter = null;
  if (areas) {
    mapCenter = [...areas.features[0].geometry.coordinates[0][0]];
    mapCenter.reverse();
  }

  return (
    <>
      <div className="modal add-car-modal is-active">
        <div className="modal-background"></div>
        <div className="modal-card">
          <header className="modal-card-head">
            <div className="modal-card-title">
              {formatMessage({ id: "adicionar-gleba" })}
            </div>
            <MdClose
              data-testid="close-button"
              className="close-button"
              onClick={onClose}
            />
          </header>
          <div className="modal-card-body">
            {step === "start" && (
              <div className="start-step">
                <p>{formatMessage({ id: "como-deseja-add-gleba" })}</p>

                {options.canDrawOnMap && (
                  <button
                    onClick={() => {
                      onSuccess("draw");
                      setStep("draw");
                    }}
                  >
                    <MdCreate /> {formatMessage({ id: "desenhar-mapa" })}
                  </button>
                )}

                {options.canUploadFile && (
                  <button onClick={() => setStep("import-file")}>
                    <MdFileUpload />{" "}
                    {formatMessage({ id: "fazer-upload-arquivo" })}
                  </button>
                )}
              </div>
            )}

            {step === "import-file" && (
              <div className="import-file-step">
                {!filename && (
                  <>
                    <p>
                      {formatMessage({
                        id: "aceitamos-formatos"
                      })}
                    </p>
                    {options.fileFormats.includes(".zip") && (
                      <li>
                        {formatMessage(
                          {
                            id: "formato-shapefile"
                          },
                          {
                            link: (
                              <button
                                className="link-button"
                                onClick={downloadShapeFile}
                              >
                                {formatMessage({ id: "baixar-template" })}
                              </button>
                            )
                          }
                        )}
                      </li>
                    )}
                    {options.fileFormats.includes(".kml") && (
                      <li>
                        {formatMessage(
                          {
                            id: "formato-kml"
                          },
                          {
                            link: (
                              <button
                                className="link-button"
                                onClick={downloadKml}
                              >
                                {formatMessage({ id: "baixar-template" })}
                              </button>
                            )
                          }
                        )}
                      </li>
                    )}

                    <label
                      className="full-width upload-button"
                      htmlFor="file-input"
                    >
                      <MdFileUpload size={20} />{" "}
                      {formatMessage({ id: "fazer-upload-glebas" })}
                    </label>
                    <input
                      type="file"
                      className="hidden-input"
                      id="file-input"
                      accept={options.fileFormats.join(",")}
                      onChange={uploadFile}
                    />
                  </>
                )}

                {filename && (
                  <div className="tile is-12 is-vertical">
                    <p>
                      {formatMessage({ id: "glebas-identificadas-arquivo" })}
                    </p>

                    <div className="identified-areas">
                      <span className="identified-areas-header">
                        {filename}
                      </span>

                      <div className="identified-areas-list">
                        {areas &&
                          areas.features.map((feature, featureIdx) => (
                            <div
                              className="identified-areas-item"
                              key={featureIdx}
                            >
                              <input
                                type="checkbox"
                                id={`gleba-${featureIdx}`}
                                value={featureIdx}
                                checked={selectedAreas.includes(featureIdx)}
                                onChange={e => {
                                  const checked = e.target.checked;
                                  if (checked) {
                                    setSelectedAreas(oldSelectedAreas => [
                                      ...oldSelectedAreas,
                                      featureIdx
                                    ]);
                                  } else {
                                    setSelectedAreas(oldSelectedAreas =>
                                      oldSelectedAreas.filter(
                                        selectedArea =>
                                          selectedArea !== featureIdx
                                      )
                                    );
                                  }
                                }}
                              />
                              <label htmlFor={`gleba-${featureIdx}`}>
                                {formatMessage({ id: "gleba" })}{" "}
                                {featureIdx + 1} -{" "}
                                {formatMessage({ id: "area" })}{" "}
                                {new Intl.NumberFormat("pt-BR", {
                                  maximumFractionDigits: 2
                                }).format(feature.properties.area_ha)}{" "}
                                ha
                              </label>
                            </div>
                          ))}
                      </div>
                    </div>

                    <MapContainer
                      fullscreenControl
                      center={mapCenter}
                      zoom={15}
                      style={{
                        width: "100%",
                        height: "450px"
                      }}
                    >
                      <TileLayer
                        url="http://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}"
                        minZoom="10"
                        subdomains={["mt0", "mt1", "mt2", "mt3"]}
                      />

                      <MapCache />

                      {areas.features.map((feature, index) => (
                        <GeoJSON key={index} data={feature.geometry} />
                      ))}
                    </MapContainer>

                    <button
                      className="remove-button"
                      onClick={() => {
                        setAreas(null);
                        setSelectedAreas([])
                        setFilename(null);
                      }}
                    >
                      <MdDelete size={20} />{" "}
                      {formatMessage({ id: "remover-arquivo" })}
                    </button>
                    <button
                      type="button"
                      role="button"
                      className={`save-button`}
                      style={
                        selectedAreas.length === 0 ? { backgroundColor: 'gray' } : {}
                      }
                      onClick={() => saveAreas()}
                      disabled={selectedAreas.length === 0}
                    >
                      {isLoading ? (
                        <>{formatMessage({ id: "carregando" })}...</>
                      ) : (
                        <>
                          {formatMessage({ id: options.continueSaveButtonId })}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AddAreaToCarModal;
